/**
 * The builder's "Which iQ field does this answer go to?" (F9): the iQ fields a
 * question can be sent to, a suggestion from its wording, and reading/writing
 * a question's mapping in the form settings.
 */

import type { FieldDef } from './fields.js';
import type { FormSettings, FormType, IqEntity, IqMappingEntry } from './forms.js';
import { iqEntityForType, resolveIqApprovalMappings, resolveIqMappings } from './forms.js';
import { IQ_SPEC_FIELDS } from './iq-fields.generated.js';

export { IQ_SPEC_FIELDS };

/** Which settings list a question's mapping lives in. */
export type IqMappingKey = 'iqMappings' | 'iqApprovalMappings';

/**
 * Accounts-stage staff questions on credit forms update the customer when the
 * credit is approved (stage 2); everything else is sent when the record is
 * created.
 */
export function iqMappingKeyFor(field: FieldDef, formType: FormType): IqMappingKey {
  return formType === 'credit_account' && field.audience === 'staff' && field.staffStage === 'accounts'
    ? 'iqApprovalMappings'
    : 'iqMappings';
}

/** The settings list's entries, migrating the legacy `iqMapping` shape. */
export function iqMappingsOf(settings: FormSettings, key: IqMappingKey): IqMappingEntry[] {
  return key === 'iqApprovalMappings' ? resolveIqApprovalMappings(settings) : resolveIqMappings(settings);
}

/** The iQ field a question is sent to directly, or '' if none. */
export function iqTargetOf(settings: FormSettings, key: IqMappingKey, fieldId: string): string {
  const m = iqMappingsOf(settings, key).find((x) => x.source.type === 'field' && x.source.fieldId === fieldId);
  return m?.target ?? '';
}

/**
 * Settings patch that sends a question to `target` (or stops sending it when
 * '' ). Only the question's first direct `field` mapping changes, in place;
 * derived, contact, constant and other entries (including a second mapping
 * of the same question) are kept as they are.
 */
export function setIqTarget(
  settings: FormSettings,
  key: IqMappingKey,
  fieldId: string,
  target: string,
): Partial<FormSettings> {
  const list = iqMappingsOf(settings, key);
  const i = list.findIndex((m) => m.source.type === 'field' && m.source.fieldId === fieldId);
  const t = target.trim();
  const next =
    i < 0
      ? t
        ? [...list, { target: t, source: { type: 'field' as const, fieldId } }]
        : list
      : t
        ? list.map((m, j) => (j === i ? { ...m, target: t } : m))
        : list.filter((_, j) => j !== i);
  // Writing the list replaces the legacy `iqMapping`; drop it so removing the
  // last mapping can't bring the old ones back.
  return key === 'iqMappings' ? { iqMappings: next, iqMapping: undefined } : { [key]: next };
}

/**
 * iQ fields to offer for a form: the spec's list for its record type, plus any
 * target the form already uses (the spec can lag behind live iQ), sorted.
 */
export function iqFieldOptions(
  formType: FormType,
  settings: FormSettings,
  /** The API's own field list, read live from its spec (falls back to the built-in one). */
  live?: IqSpecFields | null,
): string[] {
  const entity: IqEntity = iqEntityForType(formType);
  const used = [...resolveIqMappings(settings), ...resolveIqApprovalMappings(settings)].map((m) => m.target);
  const fromSpec = live?.[entity]?.length ? live[entity]! : IQ_SPEC_FIELDS[entity];
  return [...new Set<string>([...fromSpec, ...used])].sort((a, b) => a.localeCompare(b));
}

/** iQ field paths per record type, as read from the API's OpenAPI spec. */
export type IqSpecFields = Partial<Record<IqEntity | 'contact', string[]>>;

/** iQ's contact properties when the API's spec can't be read (23/10/2026). */
export const CONTACT_IQ_FIELDS = ['FirstName', 'LastName', 'FullName', 'JobTitle', 'EmailAddress', 'Phone', 'Mobile', 'Address'];

/** The iQ contact properties to offer for a contact line (the API's own, else the usual ones). */
export function iqContactFieldOptions(live?: IqSpecFields | null): string[] {
  return live?.contact?.length ? live.contact : CONTACT_IQ_FIELDS;
}

const CONTACT_SUGGEST: [RegExp, string][] = [
  [/first/i, 'FirstName'],
  [/last|surname/i, 'LastName'],
  [/full|^name$/i, 'FullName'],
  [/mobile|cell/i, 'Mobile'],
  [/e-?mail|^mail$/i, 'EmailAddress'],
  [/phone|tel/i, 'Phone'],
  [/job|position|role/i, 'JobTitle'],
];

/**
 * The iQ contact property a contact line goes to when none is chosen: from
 * its id (the built-in lines, e.g. `first_name`), else its wording ("First
 * name", "Mobile"). Addresses go to `Address`. Null when nothing matches:
 * the line isn't sent rather than going under its internal id.
 */
export function suggestContactField(sub: { id: string; label: string; type: string }): string | null {
  if (sub.type === 'address') return 'Address';
  for (const text of [sub.id, sub.label.trim()]) {
    const hit = CONTACT_SUGGEST.find(([re]) => re.test(text));
    if (hit) return hit[1];
  }
  return null;
}

/** Whether a typed iQ field looks like one (PascalCase parts, e.g. `TaxSetup.EoriNumber`). */
export function looksLikeIqField(name: string): boolean {
  return /^[A-Z][A-Za-z0-9_]*(\.[A-Z][A-Za-z0-9_]*)*$/.test(name.trim());
}

const SPEC_SKIP = new Set(['ID', 'UpdatedOn', 'CreatedOn']);
const SPEC_ENTITIES: [IqEntity, string][] = [
  ['customer', 'Customer'],
  ['supplier', 'Supplier'],
  ['lead', 'Lead'],
];

/**
 * The field paths of each record type in a parsed OpenAPI spec, one level into
 * nested objects (e.g. `TaxSetup.EoriNumber`); lists such as `Contacts` are
 * mapped whole. Same rules as `api/scripts/iq-fields-from-openapi.py`, which
 * makes the built-in list.
 */
export function iqFieldsFromSpec(spec: unknown): IqSpecFields {
  type Schema = {
    type?: string;
    properties?: Record<string, Schema>;
    items?: Schema;
    $ref?: string;
    allOf?: Schema[];
    oneOf?: Schema[];
    anyOf?: Schema[];
  };
  const paths = (schema: Schema, prefix = '', depth = 0): string[] => {
    const out: string[] = [];
    for (const [name, p] of Object.entries(schema.properties ?? {})) {
      if (SPEC_SKIP.has(name) || !p || typeof p !== 'object') continue;
      const full = prefix + name;
      if (p.type === 'array') out.push(full);
      else if (p.type === 'object' || p.properties) out.push(full, ...(depth < 1 ? paths(p, `${full}.`, depth + 1) : []));
      else out.push(full);
    }
    return out;
  };
  const schemas = (spec as { components?: { schemas?: Record<string, Schema> } } | null)?.components?.schemas ?? {};
  const out: IqSpecFields = {};
  for (const [entity, name] of SPEC_ENTITIES) if (schemas[name]) out[entity] = paths(schemas[name]!);
  // A contact's properties: what a record's Contacts list holds, following
  // $refs (to the list or its items) and allOf / oneOf wrappers; else the
  // schema that looks most like a contact (a name with "Contact" and
  // FirstName / LastName / EmailAddress properties).
  const resolve = (s: Schema | undefined, depth = 0): Schema | undefined => {
    if (!s || depth > 6) return s;
    if (s.$ref) return resolve(schemas[s.$ref.split('/').pop() ?? ''], depth + 1);
    if (s.allOf?.length) {
      const parts = s.allOf.map((x) => resolve(x, depth + 1));
      return { ...s, properties: Object.assign({}, s.properties, ...parts.map((x) => x?.properties ?? {})) };
    }
    const alt = s.oneOf ?? s.anyOf;
    if (alt?.length && !s.properties) return alt.map((x) => resolve(x, depth + 1)).find((x) => x?.properties) ?? s;
    return s;
  };
  const itemsOf = (s: Schema | undefined): Schema | undefined => {
    const r = resolve(s);
    return r?.items ? resolve(r.items) : r;
  };
  const CONTACTY = ['FirstName', 'LastName', 'EmailAddress'];
  const score = (s: Schema | undefined) => CONTACTY.filter((k) => s?.properties?.[k]).length;
  const fromRecords = SPEC_ENTITIES.map(([, name]) => itemsOf(resolve(schemas[name])?.properties?.Contacts)).find(
    (s) => score(s) > 0,
  );
  const byName = Object.entries(schemas)
    .filter(([name]) => /contact/i.test(name))
    .map(([, s]) => resolve(s))
    .filter((s) => score(s) > 0)
    .sort((a, b) => score(b) - score(a))[0];
  const contact = fromRecords ?? byName;
  if (contact?.properties) {
    out.contact = Object.keys(contact.properties).filter((k) => !SPEC_SKIP.has(k));
  }
  return out;
}

const SUGGEST: [RegExp, string][] = [
  [/trading name/i, 'TradingName'],
  [/registration number|company number|companies house/i, 'CompanyRegistration'],
  [/company name|supplier name|business name/i, 'Name'],
  [/\bvat\b/i, 'TaxSetup.TaxRegistrationNumber'],
  [/\beori\b/i, 'TaxSetup.EoriNumber'],
  [/\btss\b/i, 'TaxSetup.D_TSSCode'],
  [/sort code/i, 'BankAccountDetails.SortCode'],
  [/account number/i, 'BankAccountDetails.AccountNumber'],
  [/account name/i, 'BankAccountDetails.AccountName'],
  [/bank name/i, 'BankAccountDetails.BankName'],
  [/\biban\b/i, 'BankAccountDetails.IBAN'],
  [/\bbic\b|swift/i, 'BankAccountDetails.BIC'],
  [/credit limit/i, 'CreditControlSetup.CreditLimit'],
  [/credit terms?|payment terms/i, 'CreditControlSetup.CreditTermDefault'],
  [/currency/i, 'DefaultCurrency'],
  [/sales ?rep/i, 'SalesRep'],
  [/website|web site|home ?page/i, 'HomePage'],
  [/e-?mail/i, 'EmailAddress'],
  [/phone|telephone/i, 'Phone'],
  [/address/i, 'Address'],
];

/** A likely iQ field for a question's wording, if one exists for the form's record type. */
export function suggestIqField(label: string, options: readonly string[]): string {
  const hit = SUGGEST.find(([re, target]) => re.test(label) && options.includes(target));
  return hit ? hit[1] : '';
}
